import { cva, type VariantProps } from "class-variance-authority"
import { CheckIcon, ChevronDown, Plus, WandSparkles, XCircle, XIcon } from "lucide-react"
import * as React from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Separator } from "@/components/ui/separator"
import { Typography } from "@/components/ui/typography"
import { cn } from "@/lib/utils"

/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
const multiSelectVariants = cva("m-1 transition ease-in-out delay-150 duration-300", {
  variants: {
    variant: {
      default: "border border-border text-foreground bg-card hover:bg-card/80",
      secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
      destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/80",
      inverted: "inverted",
    },
  },
  defaultVariants: {
    variant: "default",
  },
})

/**
 * Props for MultiSelect component
 */
interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof multiSelectVariants> {
  /**
   * An array of option objects to be displayed in the multi-select component.
   * Each option object has a label, value, and an optional icon.
   */
  options: {
    /** The text to display for the option. */
    label: string
    /** The unique value associated with the option. */
    value: string
    /** Optional group name for categorizing options. */
    group?: string
    /** Optional icon component to display alongside the option. */
    icon?: React.ComponentType<{ className?: string }>
  }[]

  /**
   * Callback function triggered when the selected values change.
   * Receives an array of the new selected values.
   */
  onValueChange: (value: string[]) => void

  /** The default selected values when the component mounts. */
  defaultValue?: string[]

  /** The controlled selected values. When provided, the component becomes controlled. */
  value?: string[]

  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Select options".
   */
  placeholder?: string

  /**
   * Animation duration in seconds for the visual effects (e.g., bouncing badges).
   * Optional, defaults to 0 (no animation).
   */
  animation?: number

  /**
   * Maximum number of items to display. Extra selected items will be summarized.
   * Optional, defaults to 3.
   */
  maxCount?: number

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean

  /**
   * If true, renders the multi-select component as a child of another component.
   * Optional, defaults to false.
   */
  asChild?: boolean

  /**
   * Additional class names to apply custom styles to the multi-select component.
   * Optional, can be used to add custom styles.
   */
  className?: string

  /**
   * Allow creating new options by typing them in the search input.
   * Optional, defaults to false.
   */
  allowCreate?: boolean

  /**
   * Label for the create option when allowCreate is true.
   * Optional, defaults to "Create".
   */
  createLabel?: string

  /**
   * If true, shows the group name in the badge.
   * Optional, defaults to false.
   */
  showGroupInBadge?: boolean
}

interface OptionRendererProps {
  options: MultiSelectProps["options"]
  selectedValues: string[]
  toggleOption: (option: string) => void
}

/**
 * Component to render options with or without groups
 */
const OptionRenderer: React.FC<OptionRendererProps> = ({
  options,
  selectedValues,
  toggleOption,
}) => {
  // Check if any options have groups
  const hasGroups = options.some((option) => option.group)

  if (!hasGroups) {
    // If no groups, render options as before
    return (
      <>
        {options.map((option) => {
          const isSelected = selectedValues.includes(option.value)
          return (
            <CommandItem
              key={option.value}
              onSelect={() => toggleOption(option.value)}
              className="cursor-pointer"
            >
              <div
                className={cn(
                  "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                  isSelected
                    ? "bg-primary text-primary-foreground"
                    : "opacity-50 [&_svg]:invisible",
                )}
              >
                <CheckIcon className="h-4 w-4" />
              </div>
              {option.icon && <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />}
              <span>{option.label}</span>
            </CommandItem>
          )
        })}
      </>
    )
  }

  // If groups exist, organize and render by group
  const groups = Array.from(
    new Set(options.map((option) => option.group).filter((group): group is string => !!group)),
  )

  const ungroupedOptions = options.filter((option) => !option.group)

  return (
    <>
      {/* Render ungrouped options first */}
      {ungroupedOptions.length > 0 && (
        <>
          {ungroupedOptions.map((option) => {
            const isSelected = selectedValues.includes(option.value)
            return (
              <CommandItem
                key={option.value}
                onSelect={() => toggleOption(option.value)}
                className="cursor-pointer"
              >
                <div
                  className={cn(
                    "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                    isSelected
                      ? "bg-primary text-primary-foreground"
                      : "opacity-50 [&_svg]:invisible",
                  )}
                >
                  <CheckIcon className="h-4 w-4" />
                </div>
                {option.icon && <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />}
                <span>{option.label}</span>
              </CommandItem>
            )
          })}
          {groups.length > 0 && <CommandSeparator />}
        </>
      )}

      {/* Render each group */}
      {groups.map((group) => (
        <React.Fragment key={group}>
          <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">{group}</div>
          {options
            .filter((option) => option.group === group)
            .map((option) => {
              const isSelected = selectedValues.includes(option.value)
              return (
                <CommandItem
                  key={option.value}
                  onSelect={() => toggleOption(option.value)}
                  className="cursor-pointer"
                >
                  <div
                    className={cn(
                      "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                      isSelected
                        ? "bg-primary text-primary-foreground"
                        : "opacity-50 [&_svg]:invisible",
                    )}
                  >
                    <CheckIcon className="h-4 w-4" />
                  </div>
                  {option.icon && <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />}
                  <span>{option.label}</span>
                </CommandItem>
              )
            })}
          {/* Add separator between groups */}
          {groups.indexOf(group) < groups.length - 1 && <CommandSeparator />}
        </React.Fragment>
      ))}
    </>
  )
}

export const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options,
      onValueChange,
      variant,
      defaultValue = [],
      value,
      placeholder = "Select options",
      animation = 0,
      maxCount = 3,
      modalPopover = true,
      asChild = false,
      className,
      allowCreate = false,
      createLabel = "Create",
      showGroupInBadge = false,
      ...props
    },
    ref,
  ) => {
    const [selectedValues, setSelectedValues] = React.useState<string[]>(defaultValue)
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false)
    const [isAnimating, setIsAnimating] = React.useState(false)
    const [searchValue, setSearchValue] = React.useState("")

    // Sync internal state when controlled value changes
    React.useEffect(() => {
      if (value !== undefined) {
        setSelectedValues(value)
      }
    }, [value])

    // Use controlled value if provided, otherwise use internal state
    const currentSelectedValues = value ?? selectedValues ?? []

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        setIsPopoverOpen(true)
      } else if (event.key === "Backspace" && !event.currentTarget.value) {
        const newSelectedValues = [...currentSelectedValues]
        newSelectedValues.pop()
        if (!value) setSelectedValues(newSelectedValues)
        onValueChange(newSelectedValues)
      }
    }

    const toggleOption = (option: string) => {
      const newSelectedValues = currentSelectedValues.includes(option)
        ? currentSelectedValues.filter((value) => value !== option)
        : [...currentSelectedValues, option]
      if (!value) setSelectedValues(newSelectedValues)
      onValueChange(newSelectedValues)
    }

    const createNewOption = (newValue: string) => {
      const newSelectedValues = [...currentSelectedValues, newValue]
      if (!value) setSelectedValues(newSelectedValues)
      onValueChange(newSelectedValues)
      setSearchValue("")
    }

    const handleClear = () => {
      if (!value) setSelectedValues([])
      onValueChange([])
    }

    const handleTogglePopover = () => {
      setIsPopoverOpen((prev) => !prev)
    }

    const clearExtraOptions = () => {
      const newSelectedValues = currentSelectedValues.slice(0, maxCount)
      if (!value) setSelectedValues(newSelectedValues)
      onValueChange(newSelectedValues)
    }

    const toggleAll = () => {
      if (currentSelectedValues.length === options.length) {
        handleClear()
      } else {
        const allValues = options.map((option) => option.value)
        if (!value) setSelectedValues(allValues)
        onValueChange(allValues)
      }
    }

    const filteredOptions = options.filter(
      (option) => !searchValue || option.label.toLowerCase().includes(searchValue.toLowerCase()),
    )

    const showCreateOption =
      allowCreate &&
      searchValue.trim() &&
      !options.some((option) => option.label.toLowerCase() === searchValue.toLowerCase()) &&
      !currentSelectedValues.includes(searchValue)
    const buttonRef = ref
    return (
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal={modalPopover}>
        <PopoverTrigger asChild>
          <Button
            ref={buttonRef}
            {...props}
            onClick={handleTogglePopover}
            className={cn(
              "flex w-full p-1 rounded-md border min-h-9 h-auto items-center justify-between bg-inherit hover:bg-inherit [&_svg]:pointer-events-auto",
              className,
            )}
          >
            {currentSelectedValues.length > 0 ? (
              <div className="flex justify-between items-center w-full">
                <div className="flex flex-wrap items-center">
                  {currentSelectedValues.slice(0, maxCount).map((value) => {
                    const option = options.find((o) => o.value === value)
                    const IconComponent = option?.icon
                    return (
                      <Badge
                        key={value}
                        className={cn(
                          isAnimating ? "animate-bounce" : "",
                          multiSelectVariants({ variant }),
                        )}
                        style={{ animationDuration: `${animation}s` }}
                      >
                        {IconComponent && <IconComponent className="h-4 w-4 mr-2" />}
                        {option?.label || value}
                        {showGroupInBadge && (
                          <Typography as="span" className="text-muted-foreground text-xs ml-1">
                            ({option?.group})
                          </Typography>
                        )}
                        {!props.disabled && (
                          <XCircle
                            className="ml-2 h-4 w-4 cursor-pointer"
                            onClick={(event) => {
                              event.stopPropagation()
                              toggleOption(value)
                            }}
                          />
                        )}
                      </Badge>
                    )
                  })}
                  {currentSelectedValues.length > maxCount && (
                    <Badge
                      className={cn(
                        "bg-transparent text-foreground border-foreground/1 hover:bg-transparent",
                        isAnimating ? "animate-bounce" : "",
                        multiSelectVariants({ variant }),
                      )}
                      style={{ animationDuration: `${animation}s` }}
                    >
                      {`+ ${currentSelectedValues.length - maxCount} more`}
                      {!props.disabled && (
                        <XCircle
                          className="ml-2 h-4 w-4 cursor-pointer"
                          onClick={(event) => {
                            event.stopPropagation()
                            clearExtraOptions()
                          }}
                        />
                      )}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  {!props.disabled && (
                    <XIcon
                      className="h-4 mx-2 cursor-pointer text-muted-foreground"
                      onClick={(event) => {
                        event.stopPropagation()
                        handleClear()
                      }}
                    />
                  )}
                  <Separator orientation="vertical" className="flex min-h-6 h-full my-0" />
                  <ChevronDown
                    className={cn(
                      "h-4 mx-2 text-muted-foreground",
                      props.disabled ? "cursor-default" : "cursor-pointer",
                    )}
                  />
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between w-full h-full mx-auto">
                <span className="text-sm font-normal text-muted-foreground/60 mx-3">
                  {placeholder}
                </span>
                <ChevronDown
                  className={cn(
                    "h-4 mx-2 text-muted-foreground/60",
                    props.disabled ? "cursor-default" : "cursor-pointer",
                  )}
                />
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0"
          align="start"
          style={{ width: "var(--radix-popover-trigger-width)" }}
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
        >
          <Command>
            <CommandInput
              placeholder="Search..."
              value={searchValue}
              onValueChange={setSearchValue}
              onKeyDown={handleInputKeyDown}
            />
            <CommandList>
              <CommandEmpty>
                {showCreateOption ? (
                  <CommandItem
                    onSelect={() => createNewOption(searchValue)}
                    className="cursor-pointer"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {createLabel} "{searchValue}"
                  </CommandItem>
                ) : (
                  "No results found."
                )}
              </CommandEmpty>
              <CommandGroup>
                <CommandItem key="all" onSelect={toggleAll} className="cursor-pointer">
                  <div
                    className={cn(
                      "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                      currentSelectedValues.length === options.length
                        ? "bg-primary text-primary-foreground"
                        : "opacity-50 [&_svg]:invisible",
                    )}
                  >
                    <CheckIcon className="h-4 w-4" />
                  </div>
                  <span>(Select All)</span>
                </CommandItem>
                <OptionRenderer
                  options={filteredOptions}
                  selectedValues={currentSelectedValues}
                  toggleOption={toggleOption}
                />
                {showCreateOption && (
                  <>
                    <CommandSeparator />
                    <CommandItem
                      onSelect={() => createNewOption(searchValue)}
                      className="cursor-pointer"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      {createLabel} "{searchValue}"
                    </CommandItem>
                  </>
                )}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <div className="flex items-center justify-between">
                  {currentSelectedValues.length > 0 && (
                    <>
                      <CommandItem
                        onSelect={handleClear}
                        className="flex-1 justify-center cursor-pointer"
                      >
                        Clear
                      </CommandItem>
                      <Separator orientation="vertical" className="flex min-h-6 h-full my-0" />
                    </>
                  )}
                  <CommandItem
                    onSelect={() => setIsPopoverOpen(false)}
                    className="flex-1 justify-center cursor-pointer max-w-full"
                  >
                    Close
                  </CommandItem>
                </div>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
        {animation > 0 && currentSelectedValues.length > 0 && (
          <WandSparkles
            className={cn(
              "cursor-pointer my-2 text-foreground bg-background w-3 h-3",
              isAnimating ? "" : "text-muted-foreground",
            )}
            onClick={() => setIsAnimating(!isAnimating)}
          />
        )}
      </Popover>
    )
  },
)

MultiSelect.displayName = "MultiSelect"
